import type { Timestamp } from "firebase/firestore";

export type PlayerAccountType = "anonymous" | "google";
export type AliasType = "generated" | "chosen";
export type PublicPlayerType = "guest" | "player";
export type GameOutcome = "won" | "lost";
export type ValidationStatus = "accepted" | "review" | "rejected";

export interface PlayerDocument {
  uid: string;
  accountType: PlayerAccountType;
  publicPlayerId: string;
  publicAlias: string | null;
  aliasNormalized: string | null;
  aliasType: AliasType | null;
  googleDisplayName: string | null;
  googlePhotoURL: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastActiveAt: Timestamp;
  privateBestScore: number | null;
  privateBestAchievedAt: Timestamp | null;
  privateLatestScore: number | null;
  privateLatestFinishedAt: Timestamp | null;
  resultWindowStartedAt: Timestamp | null;
  resultWindowCount: number;
  expiresAt: Timestamp | null;
}

export interface PublicAliasDocument {
  publicPlayerId: string;
  ownerUid: string;
  aliasType: AliasType;
  createdAt: Timestamp;
}

export interface GameResultDocument {
  gameSessionId: string;
  uid: string;
  accountTypeAtFinish: PlayerAccountType;
  score: number;
  outcome: GameOutcome;
  durationMs: number;
  gameVersion: string;
  validationStatus: ValidationStatus;
  finishedAt: Timestamp;
  createdAt: Timestamp;
  expiresAt: Timestamp | null;
}

export interface PublicLeaderboardDocument {
  publicAlias: string;
  playerType: PublicPlayerType;
  bestScore: number;
  achievedAt: Timestamp;
  updatedAt: Timestamp;
}

export interface SubmitGameResultInput {
  gameSessionId: string;
  score: number;
  outcome: GameOutcome;
  durationMs: number;
  gameVersion: string;
}

export const GAME_RESULT_LIMITS = {
  minDurationMs: 1_000,
  maxDurationMs: 5 * 60 * 1000,
  minWinDurationMs: 30_000,
  maxScore: 20_000,
  scoreStep: 20,
  scoreGrace: 1_000,
  maxScorePerSecond: 400,
  maxGameVersionLength: 40,
} as const;

export const ACCEPTED_GAME_VERSIONS = new Set(["0.1.0"]);

const SESSION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function normalizePublicAlias(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("hu-HU");
}

export function validateGameResultInput(value: unknown):
  | { ok: true; value: SubmitGameResultInput }
  | { ok: false; message: string } {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { ok: false, message: "Az eredmény formátuma érvénytelen." };
  }

  const candidate = value as Record<string, unknown>;
  if (typeof candidate.gameSessionId !== "string" || !SESSION_ID_PATTERN.test(candidate.gameSessionId)) {
    return { ok: false, message: "A játékazonosító érvénytelen." };
  }
  if (
    !Number.isSafeInteger(candidate.score) ||
    Number(candidate.score) < 0 ||
    Number(candidate.score) > GAME_RESULT_LIMITS.maxScore
  ) {
    return { ok: false, message: "A pontszám kívül esik az elfogadott tartományon." };
  }
  if (candidate.outcome !== "won" && candidate.outcome !== "lost") {
    return { ok: false, message: "A játék eredményállapota érvénytelen." };
  }
  if (
    !Number.isSafeInteger(candidate.durationMs) ||
    Number(candidate.durationMs) < GAME_RESULT_LIMITS.minDurationMs ||
    Number(candidate.durationMs) > GAME_RESULT_LIMITS.maxDurationMs
  ) {
    return { ok: false, message: "A játékidő kívül esik az elfogadott tartományon." };
  }
  if (
    typeof candidate.gameVersion !== "string" ||
    candidate.gameVersion.length === 0 ||
    candidate.gameVersion.length > GAME_RESULT_LIMITS.maxGameVersionLength
  ) {
    return { ok: false, message: "A játékverzió érvénytelen." };
  }
  if (!ACCEPTED_GAME_VERSIONS.has(candidate.gameVersion)) {
    return { ok: false, message: "Ez a játékverzió már nem küldhet be eredményt." };
  }
  if (Number(candidate.score) % GAME_RESULT_LIMITS.scoreStep !== 0) {
    return { ok: false, message: "A pontszám nem illeszkedik a játék pontozásához." };
  }
  if (
    candidate.outcome === "won" &&
    Number(candidate.durationMs) < GAME_RESULT_LIMITS.minWinDurationMs
  ) {
    return { ok: false, message: "A teljesítési idő nem lehet valós játékból származó érték." };
  }

  const durationSeconds = Math.ceil(Number(candidate.durationMs) / 1000);
  const durationScoreLimit =
    GAME_RESULT_LIMITS.scoreGrace + durationSeconds * GAME_RESULT_LIMITS.maxScorePerSecond;
  if (Number(candidate.score) > durationScoreLimit) {
    return { ok: false, message: "A pontszám nem reális a megadott játékidőhöz." };
  }

  return {
    ok: true,
    value: {
      gameSessionId: candidate.gameSessionId,
      score: Number(candidate.score),
      outcome: candidate.outcome,
      durationMs: Number(candidate.durationMs),
      gameVersion: candidate.gameVersion,
    },
  };
}
